import styled from 'styled-components';
import { DateTimePicker } from 'react-rainbow-components';
import Badge from 'components/Badge';
import {
    GREY_50,
    GREY_100,
    GREY_200,
    GREY_300,
    GREY_400,
    GREY_800,
    DIVIDER,
    WHITE,
    PURPLE_500,
    PURPLE_600,
    GREEN_100,
    GREEN_500,
    GREEN_600,
    GREEN_700,
    YELLOW_100,
    YELLOW_600,
    RED_100,
    RED_300,
    RED_400,
    RED_600,
} from '../../../constants';

export const TodosContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
`;

export const TodoItemWrapper = styled.div<{ $isNested?: boolean; $isCompact?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: ${(props) => (props.$isCompact ? '0.5rem 0.75rem' : '0.5rem 0.75rem')};
    border-radius: 0.5rem;
    margin-bottom: ${(props) => (props.$isNested ? '0' : '0.5rem')};
    background: ${WHITE};
    border: 1px solid ${GREY_100};
    opacity: ${(props) => (props.$isCompact ? 0.62 : 1)};
    transition: opacity 0.2s ease;

    &:hover {
        border-color: ${GREY_400};
        opacity: 1;
    }
`;

export const TodoItemRow = styled.div<{ $isCompact?: boolean }>`
    display: flex;
    align-items: center;
    gap: ${(props) => (props.$isCompact ? '0.5rem' : '0.75rem')};
`;

export const TodoItemContainer = styled.div<{ isNested?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    background: ${WHITE};
    border: 1px solid ${GREY_100};
    margin-left: ${(props) => (props.isNested ? '0' : '0')};

    &:hover {
        border-color: ${GREY_400};
    }
`;

export const StatusButton = styled.button<{ status: string; $isCompact?: boolean }>`
    width: 1.5rem;
    height: 1.5rem;
    min-width: 1.5rem;
    border-radius: 50%;
    border: 2px solid ${(props) => {
        if (props.status === 'done') return GREEN_600;
        if (props.status === 'in_progress') return PURPLE_600;
        if (props.status === 'cancelled') return RED_400;
        return GREY_300;
    }};
    background: ${(props) => {
        if (props.status === 'done') return GREEN_500;
        if (props.status === 'in_progress') return PURPLE_500;
        if (props.status === 'cancelled') return RED_300;
        return WHITE;
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;
    position: relative;
    flex-shrink: 0;

    &:hover {
        border-color: ${(props) => {
        if (props.status === 'done') return GREEN_600;
        if (props.status === 'in_progress') return PURPLE_600;
        if (props.status === 'cancelled') return RED_400;
        return GREY_400;
    }};
        background: ${(props) => {
        if (props.status === 'done') return GREEN_600;
        if (props.status === 'in_progress') return PURPLE_600;
        if (props.status === 'cancelled') return RED_400;
        return GREY_100;
    }};
        transform: scale(1.1);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.7;

        &:hover {
            transform: none;
        }
    }

    svg {
        width: 0.875rem;
        height: 0.875rem;
        color: ${WHITE};
    }
`;

export const StatusSpinner = styled.div<{ status: string }>`
    width: 0.875rem;
    height: 0.875rem;
    border: 2px solid ${(props) => {
        // For pending (white bg), use grey for visibility
        if (props.status === 'pending') return GREY_400;
        // For colored backgrounds, use white
        return WHITE;
    }};
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export const StatusGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
`;

export const StatusLabel = styled.span<{ status: string }>`
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
    line-height: 1.5rem;
    color: ${(props) => {
        if (props.status === 'done') return GREEN_600;
        if (props.status === 'in_progress') return PURPLE_500;
        if (props.status === 'cancelled') return RED_400;
        return GREY_400;
    }};
`;

export const VerticalDivider = styled.div`
    width: 1px;
    align-self: stretch;
    background: ${GREY_100};
    flex-shrink: 0;
`;

export const TodoContent = styled.div<{ $isCompact?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: ${(props) => (props.$isCompact ? '0' : '0.25rem')};
`;

export const TodoTitle = styled.h4<{ status: string }>`
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 0;
    color: ${(props) => {
        if (props.status === 'done') return GREY_400;
        if (props.status === 'cancelled') return GREY_400;
        return GREY_800;
    }};
    text-decoration: ${(props) => {
        if (props.status === 'done') return 'line-through';
        if (props.status === 'cancelled') return 'line-through';
        return 'none';
    }};
`;

export const TodoTitleRow = styled.div<{ $isCompact?: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: ${(props) => (props.$isCompact ? 'nowrap' : 'wrap')};
`;

export const TodoBadges = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
`;

export const ChildTodosToggleButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    min-width: 1.25rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${GREY_400};
    cursor: pointer;
    margin-top: 0.125rem;

    &:hover {
        background: ${GREY_100};
        color: ${GREY_800};
    }

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

export const ChildTodosToggleSpacer = styled.div`
    width: 1.25rem;
    height: 1.25rem;
    min-width: 1.25rem;
    margin-top: 0.125rem;
`;

export const TodoBadge = styled(Badge)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    font-size: 0.6875rem;
    line-height: 1.125rem;
    padding: 0.125rem 0.375rem;
    text-transform: none;
    min-width: auto;
    height: auto;
`;

const priorityColors = {
    success: {
        bg: GREEN_100,
        color: GREEN_700,
        hoverBg: GREEN_600,
        hoverColor: WHITE,
    },
    warning: {
        bg: YELLOW_100,
        color: YELLOW_600,
        hoverBg: YELLOW_600,
        hoverColor: WHITE,
    },
    error: {
        bg: RED_100,
        color: RED_600,
        hoverBg: RED_400,
        hoverColor: WHITE,
    },
};

export const PriorityBadgeButton = styled.button<{ $variant: 'success' | 'warning' | 'error' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1.125rem;
    padding: 0.125rem 0.5rem;
    text-transform: none;
    min-width: auto;
    height: auto;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
    background: ${(props) => priorityColors[props.$variant].bg};
    color: ${(props) => priorityColors[props.$variant].color};

    &:hover {
        background: ${(props) => priorityColors[props.$variant].hoverBg};
        color: ${(props) => priorityColors[props.$variant].hoverColor};
        transform: scale(1.05);
    }
`;

export const ConfirmOverlay = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border-radius: 0.375rem;
    background: ${RED_100};
    border: 1px solid ${RED_300};
`;

export const ConfirmMessage = styled.span`
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${RED_600};
`;

export const ConfirmActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
`;

export const ConfirmButton = styled.button<{ variant: 'danger' | 'secondary' }>`
    padding: 0.25rem 0.625rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    border: 1px solid ${(props) => (props.variant === 'danger' ? RED_400 : GREY_300)};
    background: ${(props) => (props.variant === 'danger' ? RED_400 : WHITE)};
    color: ${(props) => (props.variant === 'danger' ? WHITE : GREY_800)};

    &:hover {
        background: ${(props) => (props.variant === 'danger' ? RED_600 : GREY_100)};
        border-color: ${(props) => (props.variant === 'danger' ? RED_600 : GREY_400)};
    }
`;

export const TodoDescription = styled.p<{ status: string }>`
    font-size: 0.875rem;
    margin: 0;
    color: ${GREY_400};
    text-decoration: ${(props) => {
        if (props.status === 'done') return 'line-through';
        if (props.status === 'cancelled') return 'line-through';
        return 'none';
    }};
`;

export const TodoMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0;
`;

export const TodoActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const TodoFooter = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    flex-wrap: wrap;
    padding-top: 0.375rem;
    margin-top: 0.25rem;
    border-top: 1px solid ${GREY_100};
`;

const footerColorMap = {
    success: GREEN_600,
    warning: YELLOW_600,
    error: RED_400,
    default: GREY_400,
};

export const FooterItem = styled.span<{ $color: 'success' | 'warning' | 'error' | 'default' }>`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${(props) => footerColorMap[props.$color] || GREY_400};
    white-space: nowrap;
`;

export const FooterPriorityButton = styled.button<{ $color: 'success' | 'warning' | 'error' }>`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${(props) => footerColorMap[props.$color] || GREY_400};
    white-space: nowrap;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover {
        opacity: 0.7;
    }
`;

export const NestedTodosContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    margin-left: 2.25rem;
    gap: 0.5rem;
`;

export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    text-align: center;
    color: ${GREY_400};
`;

export const EmptyStateTitle = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    margin: 0.5rem 0;
`;

export const EmptyStateDescription = styled.p`
    font-size: 0.875rem;
    margin: 0;
`;

export const CreateTodoButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid ${GREY_400};
    background: ${WHITE};
    color: ${GREY_800};
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 1rem;

    &:hover {
        border-color: ${GREY_800};
        background: ${GREY_100};
    }
`;

export const CreateFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    background: ${WHITE};
    border: 1px solid ${GREY_300};
    transition: all 0.2s;

    &:focus-within {
        border-color: ${PURPLE_500};
    }
`;

export const CreateFormRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
`;

export const CreateFormInputs = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 0.5rem;
`;

export const CreateFormTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
`;

export const CreateFormInput = styled.input`
    width: 100%;
    font-size: 0.9375rem;
    font-weight: 600;
    border: none;
    outline: none;
    padding: 0;
    color: ${GREY_800};
    background: transparent;

    &::placeholder {
        color: ${GREY_400};
        font-weight: 500;
    }
`;

export const CreateFormInlineFields = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

export const CreateFormPrioritySelect = styled.select`
    border: 1px solid ${DIVIDER};
    border-radius: 0.375rem;
    padding: 0 0.75rem;
    background: ${WHITE};
    color: ${GREY_800};
    font-size: 0.8125rem;
    line-height: 2rem;
    height: 2rem;
    outline: none;
    flex-shrink: 0;

    &:focus {
        border-color: ${PURPLE_500};
    }
`;

export const CreateFormDateInput = styled.input`
    border: 1px solid ${GREY_300};
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: ${WHITE};
    color: ${GREY_800};
    font-size: 0.75rem;
    line-height: 1.25rem;
    height: 1.75rem;
    outline: none;

    &:focus {
        border-color: ${PURPLE_500};
    }
`;

export const CreateFormTextarea = styled.textarea`
    width: 100%;
    font-size: 0.875rem;
    border: none;
    outline: none;
    padding: 0;
    color: ${GREY_400};
    background: transparent;
    resize: none;
    min-height: 2.5rem;

    &::placeholder {
        color: ${GREY_300};
    }
`;

export const CreateFormActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid ${GREY_100};
`;

export const CreateFormButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid ${(props) => (props.variant === 'primary' ? PURPLE_500 : 'transparent')};
    background: ${(props) => (props.variant === 'primary' ? PURPLE_500 : 'transparent')};
    color: ${(props) => (props.variant === 'primary' ? WHITE : GREY_400)};

    &:hover {
        background: ${(props) => (props.variant === 'primary' ? PURPLE_600 : GREY_100)};
        border-color: ${(props) => (props.variant === 'primary' ? PURPLE_600 : 'transparent')};
        color: ${(props) => (props.variant === 'primary' ? WHITE : GREY_800)};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export const AddButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    border: 1.5px dashed ${GREY_200};
    background: transparent;
    color: ${GREY_400};
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 0.5rem;

    &:hover {
        border-color: ${PURPLE_500};
        color: ${PURPLE_500};
        background: ${GREY_50};
    }

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

export const CreateFormAssigneeWrapper = styled.div`
    margin-top: 0;
    display: inline-flex;
    align-self: flex-start;
    width: fit-content;
    max-width: 100%;
`;

export const QuickAddContainer = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    background: ${WHITE};
    border: 1.5px solid ${GREY_300};
    transition: all 0.2s;

    &:focus-within {
        border-color: ${PURPLE_500};
    }
`;

export const QuickAddRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
`;

export const QuickAddInput = styled.input`
    flex: 1;
    font-size: 0.9375rem;
    font-weight: 500;
    border: none;
    outline: none;
    padding: 0;
    color: ${GREY_800};
    background: transparent;

    &::placeholder {
        color: ${GREY_400};
        font-weight: 400;
    }
`;

export const QuickAddHint = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    color: ${GREY_300};
    white-space: nowrap;
    flex-shrink: 0;
    user-select: none;
`;

export const DetailsToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0;
    border: none;
    background: transparent;
    color: ${GREY_400};
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
        color: ${PURPLE_500};
    }

    svg {
        width: 0.875rem;
        height: 0.875rem;
        transition: transform 0.2s;
    }
`;

export const DetailsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem 0.75rem;
    border-top: 1px solid ${GREY_100};
`;

export const DetailsFieldsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const QuickAddActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-top: 1px solid ${GREY_100};
`;

export const CreateAnotherLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${GREY_800};
    cursor: pointer;
    user-select: none;
    margin-right: auto;

    input[type="checkbox"] {
        width: 1rem;
        height: 1rem;
        accent-color: ${PURPLE_500};
        cursor: pointer;
    }
`;

export const AssigneePickerWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

export const StyledDateTimePicker = styled(DateTimePicker)`
    flex: 1;
    min-width: 0;
    overflow: hidden;

    input {
        border-radius: 0.375rem;
        border: 1px solid ${DIVIDER};
        font-size: 0.8125rem;
        height: 2rem;
        line-height: 2rem;
        padding: 0 0.75rem;
        width: 100%;
        text-overflow: ellipsis;

        &::placeholder {
            font-weight: 300 !important;
            font-size: 0.8125rem;
        }
    }

    label {
        margin-bottom: 0;
    }
`;

export const StyledAssigneeWrapper = styled.div`
    flex-shrink: 0;

    input {
        border-radius: 0.375rem;
        border: 1px solid ${DIVIDER};
        font-size: 0.8125rem;
        height: 2rem;
        line-height: 2rem;
        padding: 0 0.75rem;

        &::placeholder {
            font-weight: 300 !important;
            font-size: 0.8125rem;
        }
    }

    button {
        height: 2rem;
    }
`;
